import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useAccount } from 'wagmi';
import { CPGABI, TaskEscrowABI } from '../../ABI/ABI';
import AmountDisplay from '../../components/amount/AmountDisplay';
import Loader from '../../components/loader/Loader';
import {
    CPGAddress,
    initialTokenList,
    TASK_ESCROW_ADDRESS,
    WalletAddressContext,
} from '../../constant/constant';
import { copyToClipboard, shortenWalletAddress } from '../../utils/commonFunction';
import { fetchAdminContractAnalytics } from '../../utils/adminContractAnalytics';
import { blockExplorerUrl } from '../../utils/wagmiConfig';
import AdminOnlyPage from './AdminOnlyPage';

const formatDurationHours = (seconds) => {
    const hours = Number(seconds) / 3600;
    if (!Number.isFinite(hours) || hours <= 0) return '—';
    if (hours >= 24) {
        const days = hours / 24;
        return `${days.toFixed(days >= 10 ? 0 : 1)} days`;
    }
    return `${hours.toFixed(hours >= 10 ? 0 : 1)} hours`;
};

const MetricCard = ({ label, value, sub, tone = 'zinc', loading }) => {
    const toneMap = {
        zinc: 'border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900/80',
        teal: 'border-teal-200 bg-teal-50/80 dark:border-teal-900/50 dark:bg-teal-950/35',
        violet: 'border-violet-200 bg-violet-50/80 dark:border-violet-900/50 dark:bg-violet-950/35',
        amber: 'border-amber-200 bg-amber-50/80 dark:border-amber-900/50 dark:bg-amber-950/35',
    };
    return (
        <div
            className={`rounded-2xl border p-5 shadow-sm dark:shadow-none ${toneMap[tone] || toneMap.zinc}`}
        >
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-500">
                {label}
            </p>
            <div className="mt-3 text-3xl font-semibold tabular-nums text-zinc-900 dark:text-white">
                {loading ? <Loader size="22px" /> : value}
            </div>
            {sub && (
                <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">{sub}</p>
            )}
        </div>
    );
};

const AddressRow = ({ label, address }) => {
    if (!address) return null;
    const explorer = blockExplorerUrl
        ? `${blockExplorerUrl}/address/${address}`
        : null;
    return (
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                {label}
            </span>
            <div className="flex items-center gap-2">
                <span className="font-mono text-sm text-zinc-800 dark:text-zinc-200">
                    {shortenWalletAddress(address)}
                </span>
                <button
                    type="button"
                    onClick={() => copyToClipboard(address)}
                    className="text-zinc-400 hover:text-teal-700 dark:hover:text-teal-400"
                    title="Copy address"
                >
                    <i className="fa-regular fa-copy text-xs" />
                </button>
                {explorer && (
                    <a
                        href={explorer}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-zinc-400 hover:text-teal-700 dark:hover:text-teal-400"
                        title="View on explorer"
                    >
                        <i className="fa-solid fa-arrow-up-right-from-square text-xs" />
                    </a>
                )}
            </div>
        </div>
    );
};

const StableBalanceList = ({ balances = [], loading }) => {
    if (loading) return <Loader size="20px" />;
    return (
        <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {balances.map((token) => (
                <li
                    key={token.address}
                    className="flex items-center justify-between py-3"
                >
                    <div className="flex items-center gap-2">
                        {token.logoURI ? (
                            <img
                                src={token.logoURI}
                                alt=""
                                className="h-6 w-6 rounded-full"
                            />
                        ) : (
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-zinc-100 text-[10px] font-bold dark:bg-zinc-800">
                                {token.symbol?.charAt(0)}
                            </span>
                        )}
                        <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200">
                            {token.symbol}
                        </span>
                    </div>
                    <AmountDisplay
                        value={token.balanceFloat}
                        className="text-sm font-semibold text-zinc-900 dark:text-white"
                        suffix={token.symbol}
                    />
                </li>
            ))}
        </ul>
    );
};

const AdminContractAnalytics = () => {
    const { address } = useAccount();
    const { adminWalletAddress } = useContext(WalletAddressContext);
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadAnalytics = useCallback(async () => {
        if (!address || !CPGAddress) return;
        setLoading(true);
        setError('');
        try {
            const data = await fetchAdminContractAnalytics({
                gatewayAddress: CPGAddress,
                gatewayAbi: CPGABI,
                escrowAddress: TASK_ESCROW_ADDRESS?.trim() || null,
                escrowAbi: TaskEscrowABI,
                tokenList: initialTokenList,
            });
            setAnalytics(data);
            if (!data?.gateway && !data?.jobEscrow?.configured && data?.errors?.length) {
                setError(data.errors.join(' · '));
            } else {
                setError('');
            }
        } catch (err) {
            console.error(err);
            setError(err?.message || 'Failed to load contract analytics');
        } finally {
            setLoading(false);
        }
    }, [address]);

    useEffect(() => {
        loadAnalytics();
    }, [loadAnalytics]);

    const gateway = analytics?.gateway;
    const jobEscrow = analytics?.jobEscrow;
    const escrowConfigured = Boolean(TASK_ESCROW_ADDRESS?.trim());

    return (
        <AdminOnlyPage
            title="Contract analytics"
            description="Connect the wallet that matches the on-chain gateway admin address to view live contract metrics."
        >
            <div className="crypto-page relative min-h-[calc(100vh-80px)]">
                <div className="relative z-10 mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
                    <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex gap-4">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-violet-200 bg-violet-50 text-violet-800 dark:border-violet-900/50 dark:bg-violet-950/40 dark:text-violet-300">
                                <i className="fa-solid fa-chart-line text-lg" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-semibold tracking-tight text-zinc-900 dark:text-white md:text-4xl">
                                    Contract analytics
                                </h1>
                                <p className="mt-2 max-w-2xl text-sm text-zinc-600 dark:text-zinc-400">
                                    Lightweight on-chain snapshot: USDC & EURC balances
                                    and contract counters only (minimal RPC usage).
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={loadAnalytics}
                            disabled={loading}
                            className="inline-flex items-center gap-2 self-start rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm font-semibold text-zinc-800 shadow-sm transition-colors hover:bg-zinc-50 disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:hover:bg-zinc-800"
                        >
                            <i
                                className={`fa-solid fa-rotate-right text-xs ${loading ? 'animate-spin' : ''}`}
                            />
                            Refresh
                        </button>
                    </div>

                    {error && (
                        <div className="mb-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-200">
                            {error}
                        </div>
                    )}

                    {analytics?.errors?.length > 0 && (
                        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-100">
                            {analytics.errors.join(' · ')}
                        </div>
                    )}

                    {!escrowConfigured && (
                        <div className="mb-6 rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-700 dark:border-zinc-700 dark:bg-zinc-900/50 dark:text-zinc-300">
                            Set <code className="text-xs">REACT_APP_TASK_ESCROW_ADDRESS</code>{' '}
                            in your frontend env to include job escrow analytics.
                        </div>
                    )}

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <MetricCard
                            label="Gateway records"
                            loading={loading}
                            tone="violet"
                            value={gateway?.totalTransactions ?? 0}
                            sub={gateway?.transactionLabel || 'Disputes on payment gateway'}
                        />
                        <MetricCard
                            label="Job escrows"
                            loading={loading}
                            tone="teal"
                            value={
                                escrowConfigured ? jobEscrow?.totalTransactions ?? 0 : '—'
                            }
                            sub={
                                escrowConfigured
                                    ? jobEscrow?.transactionLabel || 'Total escrows created'
                                    : 'Not configured'
                            }
                        />
                    </div>

                    <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
                        <section className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-950/50">
                            <div className="border-b border-zinc-200 px-5 py-4 dark:border-zinc-800">
                                <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">
                                    Payment gateway
                                </h2>
                                <p className="mt-0.5 text-xs text-zinc-500">
                                    USDC & EURC held · CryptoPaymentGateway
                                </p>
                            </div>
                            <div className="space-y-4 px-5 py-4">
                                <AddressRow label="Contract" address={gateway?.address} />
                                <AddressRow
                                    label="On-chain admin"
                                    address={gateway?.admin || adminWalletAddress}
                                />
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-zinc-500">Dispute window</span>
                                    <span className="font-medium text-zinc-800 dark:text-zinc-200">
                                        {formatDurationHours(
                                            gateway?.disputeResolutionPeriodSeconds,
                                        )}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-zinc-500">On-chain records</span>
                                    <span className="font-semibold tabular-nums text-zinc-900 dark:text-white">
                                        {gateway?.totalTransactions ?? 0}
                                    </span>
                                </div>
                            </div>
                            <div className="border-t border-zinc-200 px-5 py-4 dark:border-zinc-800">
                                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
                                    Token balances (USDC & EURC)
                                </p>
                                <StableBalanceList
                                    balances={gateway?.tokenBalances}
                                    loading={loading}
                                />
                            </div>
                        </section>

                        <section className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-950/50">
                            <div className="border-b border-zinc-200 px-5 py-4 dark:border-zinc-800">
                                <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">
                                    Job escrow
                                </h2>
                                <p className="mt-0.5 text-xs text-zinc-500">
                                    USDC & EURC held · CronPayJobEscrow
                                </p>
                            </div>
                            {escrowConfigured ? (
                                <>
                                    <div className="space-y-4 px-5 py-4">
                                        <AddressRow
                                            label="Contract"
                                            address={jobEscrow?.address}
                                        />
                                        <AddressRow label="Owner" address={jobEscrow?.owner} />
                                        <AddressRow
                                            label="Dispute admin"
                                            address={jobEscrow?.disputeAdmin}
                                        />
                                        <AddressRow
                                            label="Payment token"
                                            address={jobEscrow?.paymentToken}
                                        />
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-zinc-500">Paused</span>
                                            <span className="font-medium text-zinc-800 dark:text-zinc-200">
                                                {jobEscrow?.paused ? 'Yes' : 'No'}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-zinc-500">Total escrows</span>
                                            <span className="font-semibold tabular-nums text-zinc-900 dark:text-white">
                                                {jobEscrow?.totalTransactions ?? 0}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="border-t border-zinc-200 px-5 py-4 dark:border-zinc-800">
                                        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
                                            Token balances (USDC & EURC)
                                        </p>
                                        <StableBalanceList
                                            balances={jobEscrow?.tokenBalances}
                                            loading={loading}
                                        />
                                    </div>
                                </>
                            ) : (
                                <p className="px-5 py-8 text-sm text-zinc-500">
                                    Job escrow address not configured.
                                </p>
                            )}
                        </section>
                    </div>

                    {analytics?.fetchedAt && (
                        <p className="mt-6 text-center text-xs text-zinc-500 dark:text-zinc-500">
                            Last updated {new Date(analytics.fetchedAt).toLocaleString()}
                            {analytics.stableTokens?.length
                                ? ` · tokens: ${analytics.stableTokens.join(', ')}`
                                : ''}
                        </p>
                    )}
                </div>
            </div>
        </AdminOnlyPage>
    );
};

export default AdminContractAnalytics;
